import { useEffect, useState } from "react";
import { IndexApi, type IndexQuoteItem } from "@/services/api";
import { changeColor, changeSign, fmtNum } from "@/lib/format";

function TickerItem({ it }: { it: IndexQuoteItem }) {
  const isNotice = it.price <= 0; // price=0 视为提示项（如刷新失败标记）
  return (
    <span className="inline-flex items-center gap-2 px-5 text-xs">
      <span className={isNotice ? "text-gold" : "text-ink-400"}>{it.name}</span>
      {!isNotice && (
        <>
          <span className="num text-ink-100">{fmtNum(it.price)}</span>
          <span className={`num ${changeColor(it.changePct)}`}>
            {changeSign(it.changePct)}
            {it.changePct.toFixed(2)}%
          </span>
        </>
      )}
      <span className="text-ink-700">|</span>
    </span>
  );
}

export default function Ticker() {
  // null = 从未成功获取过；有数据后即使本轮刷新失败也保留最近数据并标记
  const [items, setItems] = useState<IndexQuoteItem[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await IndexApi.quote();
        if (!alive) return;
        if (data.length) {
          setItems(data);
          setFailed(false);
        } else {
          setFailed(true);
        }
      } catch {
        if (alive) setFailed(true);
      }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // 从未成功：明确显示获取失败，绝不显示假数据
  if (!items) {
    return (
      <div className="border-b border-ink-700 bg-ink-900/80 py-1.5 text-center text-xs">
        {failed ? (
          <span className="text-gold">⚠ 指数数据获取失败（每分钟自动重试）</span>
        ) : (
          <span className="text-ink-500">指数加载中…</span>
        )}
      </div>
    );
  }

  // 曾成功但本轮刷新失败：保留最近数据，并插入醒目标记提示数据可能过期
  const list = failed
    ? [{ code: "stale", name: "⚠ 指数刷新失败·以下为最近数据", price: 0, changePct: 0 }, ...items]
    : items;
  const doubled = [...list, ...list];

  return (
    <div className="relative overflow-hidden border-b border-ink-700 bg-ink-900/80 backdrop-blur">
      <div className="ticker-track animate-ticker py-1.5">
        {doubled.map((it, i) => (
          <TickerItem key={i} it={it} />
        ))}
      </div>
    </div>
  );
}
